// ─── Trading "needs attention" — single source of truth ──────────────────────
// One attention model keyed by nav node so counts CASCADE and RECONCILE: every
// parent equals the sum of its children, down to the flagged rows. The sidebar
// Trading badge, the Trading sub-nav badges, the in-page tab badges and the
// vendor-group chips all read from here, so no level can drift from another and
// resolving one item decrements every level above it.
//
// Counts are derived from the SAME React Query caches the rest of the app
// already uses (`overdue-release-count`, `online-order-kpis`, `low-stock-count`,
// `overdue-bill-count`, `unreceived-purchase-count`) — so consistency is
// automatic and invalidating any cache updates every badge at once.
//
// Only leaves carry a raw count; parents are computed by rollup. To extend the
// model later, add a leaf node + wire its count into `useTradingAttention`.

import { useMemo } from 'react'
import { useOverdueReleaseCount } from '../../hooks/useOverdueReleaseCount'
import { useOnlineOrderKpis } from '../../hooks/useOnlineOrderKpis'
import { useLowStockCount } from '../../hooks/useLowStockCount'
import { useOverdueBillCount } from '../../hooks/useOverdueBillCount'
import { useUnreceivedPurchaseCount } from '../../hooks/useUnreceivedPurchaseCount'
import { usePageVisibility } from '../permissions/usePageVisibility'
import { unattendedOnlineOrderCount } from '../supabaseQueries'

export type AttentionNodeKey =
  | 'trading'
  | 'trading.expenses'
  | 'trading.expenses.prepayments'
  | 'trading.expenses.bills'
  | 'trading.sell'
  | 'trading.sell.online_orders'
  | 'trading.inventory'
  | 'trading.inventory.low_stock'
  | 'trading.buy'
  | 'trading.buy.unreceived'

export interface AttentionNodeDef {
  key: AttentionNodeKey
  label: string
  /** Deep link to the filtered view holding exactly these flagged items. */
  link: string
  /** Child node keys; leaves have none. */
  children?: AttentionNodeKey[]
}

// The node tree. Parents list their children; leaves define where the flagged
// rows live. `link` always points to the filtered ("attention") view so a badge
// click drills straight to the items behind the number.
export const ATTENTION_NODES: Record<AttentionNodeKey, AttentionNodeDef> = {
  trading: {
    key: 'trading',
    label: 'Trading',
    link: '/expenses?tab=prepayments&attention=1',
    children: ['trading.expenses', 'trading.sell', 'trading.inventory', 'trading.buy'],
  },
  'trading.expenses': {
    key: 'trading.expenses',
    label: 'Expenses',
    link: '/expenses?tab=prepayments&attention=1',
    children: ['trading.expenses.prepayments', 'trading.expenses.bills'],
  },
  'trading.expenses.prepayments': {
    key: 'trading.expenses.prepayments',
    label: 'Releases due',
    link: '/expenses?tab=prepayments&attention=1',
  },
  'trading.expenses.bills': {
    key: 'trading.expenses.bills',
    label: 'Overdue bills',
    link: '/expenses?tab=accruals&attention=1',
  },
  'trading.sell': {
    key: 'trading.sell',
    label: 'Sell',
    link: '/sell/online-orders',
    children: ['trading.sell.online_orders'],
  },
  'trading.sell.online_orders': {
    key: 'trading.sell.online_orders',
    label: 'Online orders to action',
    link: '/sell/online-orders',
  },
  'trading.inventory': {
    key: 'trading.inventory',
    label: 'Inventory',
    link: '/inventory',
    children: ['trading.inventory.low_stock'],
  },
  'trading.inventory.low_stock': {
    key: 'trading.inventory.low_stock',
    label: 'Low / out of stock',
    link: '/inventory/low-stock',
  },
  'trading.buy': {
    key: 'trading.buy',
    label: 'Buy',
    link: '/buy/in-transit',
    children: ['trading.buy.unreceived'],
  },
  'trading.buy.unreceived': {
    key: 'trading.buy.unreceived',
    label: 'Purchases to receive',
    link: '/buy/in-transit',
  },
}

// Which nav path (used by TradingLayout / Sidebar) maps to which node.
export const SUBNAV_ATTENTION_NODE: Record<string, AttentionNodeKey> = {
  '/expenses': 'trading.expenses',
  '/sell': 'trading.sell',
  '/inventory': 'trading.inventory',
  '/buy': 'trading.buy',
}

export type AttentionCounts = Record<AttentionNodeKey, number>

/**
 * Roll leaf counts up the tree: every parent = sum of its children. Pure and
 * deterministic — this is the function that GUARANTEES reconciliation, so the
 * UI never re-sums independently.
 */
export function rollupAttention(leaves: Partial<Record<AttentionNodeKey, number>>): AttentionCounts {
  const memo = new Map<AttentionNodeKey, number>()
  function resolve(key: AttentionNodeKey): number {
    const cached = memo.get(key)
    if (cached !== undefined) return cached
    const def = ATTENTION_NODES[key]
    const value = def.children && def.children.length > 0
      ? def.children.reduce((sum, c) => sum + resolve(c), 0)
      : Math.max(0, leaves[key] ?? 0)
    memo.set(key, value)
    return value
  }
  const out = {} as AttentionCounts
  for (const key of Object.keys(ATTENTION_NODES) as AttentionNodeKey[]) out[key] = resolve(key)
  return out
}

export interface TradingAttention {
  counts: AttentionCounts
  total: number
  /** Count for any node (leaf or rollup). */
  countFor: (key: AttentionNodeKey) => number
  /** Leaf-level breakdown for a summary panel. */
  leaves: Array<{ key: AttentionNodeKey; label: string; link: string; count: number }>
  isLoading: boolean
}

// Order here drives the summary-panel line order.
export const LEAF_KEYS: AttentionNodeKey[] = [
  'trading.expenses.prepayments',
  'trading.expenses.bills',
  'trading.sell.online_orders',
  'trading.inventory.low_stock',
  'trading.buy.unreceived',
]

/**
 * The single hook every consumer calls. `onlineEnabled` gates the online-order
 * query for users who can't see the Online Orders page (mirrors the sidebar's
 * existing gate). The low-stock / bills / unreceived leaves are gated the same
 * way by page visibility, so a leaf whose page a user can't reach contributes 0
 * (and never runs its query).
 */
export function useTradingAttention(tid: string, onlineEnabled = true): TradingAttention {
  const { isPageVisible } = usePageVisibility()
  const inventoryEnabled = isPageVisible('inventory.low_stock')
  const billsEnabled = isPageVisible('expenses')
  const buyEnabled = isPageVisible('buy.in_transit')

  const { data: prepaymentOverdue = 0, isLoading: loadingPrepay } = useOverdueReleaseCount(tid)
  const { data: onlineKpis, isLoading: loadingOnline } = useOnlineOrderKpis(tid, onlineEnabled)
  const { data: lowStock = 0, isLoading: loadingLowStock } = useLowStockCount(tid, inventoryEnabled)
  const { data: overdueBills = 0, isLoading: loadingBills } = useOverdueBillCount(tid, billsEnabled)
  const { data: unreceived = 0, isLoading: loadingUnreceived } = useUnreceivedPurchaseCount(tid, buyEnabled)

  const online = onlineEnabled ? unattendedOnlineOrderCount(onlineKpis) : 0

  return useMemo(() => {
    const counts = rollupAttention({
      'trading.expenses.prepayments': prepaymentOverdue,
      'trading.expenses.bills': billsEnabled ? overdueBills : 0,
      'trading.sell.online_orders': online,
      'trading.inventory.low_stock': inventoryEnabled ? lowStock : 0,
      'trading.buy.unreceived': buyEnabled ? unreceived : 0,
    })
    const leaves = LEAF_KEYS
      .map(k => ({ key: k, label: ATTENTION_NODES[k].label, link: ATTENTION_NODES[k].link, count: counts[k] }))
      .filter(l => l.count > 0)
    return {
      counts,
      total: counts.trading,
      countFor: (key: AttentionNodeKey) => counts[key] ?? 0,
      leaves,
      isLoading:
        loadingPrepay ||
        (onlineEnabled && loadingOnline) ||
        (inventoryEnabled && loadingLowStock) ||
        (billsEnabled && loadingBills) ||
        (buyEnabled && loadingUnreceived),
    }
  }, [
    prepaymentOverdue, overdueBills, online, lowStock, unreceived,
    onlineEnabled, inventoryEnabled, billsEnabled, buyEnabled,
    loadingPrepay, loadingOnline, loadingLowStock, loadingBills, loadingUnreceived,
  ])
}
